import { User } from "@/entities/user";
import { setAdmin } from "@/entities/user/api";
import { useAuthStore } from "@/features/auth";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect, useRef } from "react";
import { Container, Col, Row, Button, Badge, InputGroup } from "react-bootstrap";
import { formatPhone, SearchInputUI } from "@/shared/ui";
import { Chat, createChat, getChats, useChatStore } from "@/entities/chat";
import { Contact, getContacts, searchContacts } from "@/entities/contact";
import './contacts.css';
import { ContactDetails } from "./contact-details";
import { useNavigate } from "react-router-dom";
import { X } from "react-bootstrap-icons";
import { useMediaQuery } from "react-responsive";

export type UserWithPasswordFields = User & {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

const ContactsPage = () => {
  const { isAdmin } = useAuthStore();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [page, setPage] = useState(1);
  const [cntacts, setContacts] = useState<Contact[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const isMobile = useMediaQuery({ maxWidth: 507 });

  useEffect(() => {
    if (typeof count === "number") {
      document.title = count > 0 ? `Контакты (${count})` : "Контакты";
    } else {
      document.title = "Контакты";
    }
  }, [count]);

  const fetchContacts = async (pageNum: number) => {
    if (loading) return;
    setLoading(true);

    try {
      if (query) {
        const res = await searchContacts(pageNum, query);

        if (res.success) {
          if (pageNum === 1) {
            setContacts(res.result);
          } else if (pageNum > 1) {
            setContacts(prev => {
              const existingIds = new Set(prev.map(c => c._id));
              const uniqueNew = res.result?.filter(c => !existingIds.has(c._id));
              return [...prev, ...uniqueNew];
            });
          }

          setCount(res.count || 0);
        } else {
          enqueueSnackbar("Не удалось загрузить контакты", { variant: "error" });
        }
      } else {
        const res = await getContacts(pageNum);
        if (res.success) {
          if (pageNum === 1) {
            setContacts(res.result);
          } else if (pageNum > 1) {
            setContacts(prev => {
              const existingIds = new Set(prev.map(c => c._id));
              const uniqueNew = res.result?.filter(c => !existingIds.has(c._id));
              return [...prev, ...uniqueNew];

            });
          }

          setCount(res.count || 0);
        } else {
          enqueueSnackbar("Не удалось загрузить контакты", { variant: "error" });
        }

      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Ошибка при загрузке контактов", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts(1);
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (
      isBottom &&
      !loading &&
      cntacts.length < count
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchContacts(nextPage);
    }
  };

  const handleSetAdmin = async (id: string) => {
    if (id) return;

    try {
      const res = await setAdmin(id);
      if (res.success) {
        enqueueSnackbar("Пользователь назначен администратором", { variant: "success" });
      } else {
        enqueueSnackbar("Не удалось назначить администратора", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Ошибка при назначении администратора", { variant: "error" });
    }
  };

  const isAdminContact = (roles: string[]) => {
    return roles.includes("ADMIN")
  }

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const handler = setTimeout(() => {
      fetchContacts(1);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  const handleClear = () => {
    setQuery("");
  };


  const createChatHandler = (firstName: string, lastName: string, phone: string) => {
createChat({ firstName, lastName, phone })
                                .then(data => {
                                  if (data.success) {
                                    useChatStore.getState().addChatForState(data.result)
                                    useChatStore.getState().selectChat(data.result)
                                    navigate(`/?chat=${data.result._id}`)
                                  }

                                })
  }

  return (
    <Container className="mt-4 contacts-page">
      {
        !(isMobile && selectedContact) && (
          <h2 className="page-title">Контакты</h2>
        )
      }

      {
        !(isMobile && selectedContact) && (
          <SearchInputUI
            value={query}
            onChange={setQuery}
            onClear={handleClear}
            placeholder="Поиск контактов..."
          />
        )
      }

      <Row className={!(isMobile && selectedContact) ? "mt-4" : "mt-0"}>
        <Col md={5} className="d-none d-md-block">
          <div className="contacts-card">
            <div onScroll={handleScroll} className="contacts-list scrollbar-custom-y">
              {cntacts.map((contact, index) => {
                const u = contact.user;

                const isSelected = selectedContact?._id === contact._id;

                return (
                  <div
                    key={contact._id + index}
                    className={`contact-item ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedContact(contact)
                    }}
                  >
                    <div className="d-flex w-100 justify-content-between">

                      <div className="d-flex flex-column">
                        <div className="contact-name">{contact.firstName} {contact.lastName}</div>
                        <div className="contact-phone">{formatPhone(u?.phone)}</div>
                      </div>

                      <Button
                        className="px-2 py-0"
                        style={{ fontSize: 11, height: 27 }}
                        variant="outline-secondary"
                        onClick={(e) => {
                          e.stopPropagation()

                          if (contact.chat) {
                            useChatStore.getState().selectChatId(contact.chat, navigate)
                          } else {
                            if (
                              contact.user?.phone
                            ) {
                              createChatHandler(
                                contact.firstName || "",contact.firstName || "",contact.user?.phone 
                              )
                            }
                          }
                        }}
                      >
                        Написать
                      </Button>

                    </div>

                    <div className="badges mt-2 d-flex gap-2">
                      {
                        isAdmin && u &&
                        <Badge bg={u?.status === "REGISTERED" ? "success" : "warning"}>
                          {u?.status === "REGISTERED" ? "Зарегистрирован" : "Приглашён"}
                        </Badge>
                      }

                      {u &&
                        <Badge bg={isAdminContact(u?.roles || []) ? "primary" : "secondary"}>
                          {isAdminContact(u?.roles || []) ? "Админ" : "Пользователь"}
                        </Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>

        <Col xs={12} className="d-md-none">
          {!selectedContact && (
            <div className="contacts-card">
              <div onScroll={handleScroll} className="contacts-list scrollbar-custom-y">
                {cntacts.map((contact, index) => {
                  const u = contact.user;

                  return (
                    <div
                      key={contact._id + index}
                      className="contact-item"
                      onClick={() => {
                        setSelectedContact(contact)
                      }}
                    >
                      <div className="d-flex w-100 justify-content-between">

                        <div className="d-flex flex-column">
                          <div className="contact-name">{contact.firstName} {contact.lastName}</div>
                          <div className="contact-phone">{formatPhone(u?.phone)}</div>
                        </div>

                        {
                          contact.chat &&
                          <Button
                            className="px-2 py-0"
                            style={{ fontSize: 11, height: 27 }}
                            variant="outline-secondary"
                            onClick={(e) => {
                              e.stopPropagation()
                              useChatStore.getState().selectChatId(contact.chat, navigate)
                            }}
                          >
                            Написать
                          </Button>
                        }

                      </div>

                      <div className="badges mt-2 d-flex gap-2">
                        {
                          isAdmin && u &&
                          <Badge bg={u?.status === "REGISTERED" ? "success" : "warning"}>
                            {u?.status === "REGISTERED" ? "Зарегистрирован" : "Приглашён"}
                          </Badge>
                        }
                        {
                          u &&
                          <Badge bg={isAdminContact(u?.roles || []) ? "primary" : "secondary"}>
                            {isAdminContact(u?.roles || []) ? "Админ" : "Пользователь"}
                          </Badge>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Col>


        <Col md={7} className="d-none d-md-block">
          <ContactDetails
          createChat={createChatHandler}
            selectedContact={selectedContact}
            chatId={selectedContact?.chat}
            isAdmin={isAdmin}
            handleSetAdmin={handleSetAdmin}
          />
        </Col>

        <Col xs={12} className="d-md-none">
          {selectedContact && (
            <div className="contact-details-card">

              <Button
                variant="secondary"
                size="sm"
                className="mb-3"
                onClick={() => setSelectedContact(null)}
              >
                Назад
              </Button>

              <ContactDetails
          createChat={createChatHandler}
                selectedContact={selectedContact}
                chatId={selectedContact?.chat}
                isAdmin={isAdmin}
                handleSetAdmin={handleSetAdmin}
              />
            </div>
          )}
        </Col>

      </Row>
    </Container>
  );
};

export default ContactsPage;